// src/CreateClinicEvent.js
import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "./CreateClinicEvent.module.css";
import Swal from 'sweetalert2';

import { db } from "./firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { onSnapshot } from "firebase/firestore";



function CreateClinicEvent() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
const [eventsForSelectedDay, setEventsForSelectedDay] = useState([]);
  const [eventData, setEventData] = useState({
    title: "",
    location: "",
    address: "",
    startTime: "",
    endTime: "",
    maxStudents: "",
    notes: "",
    cohorts: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCohortToggle = (cohort) => {
    setEventData((prev) => {
      const cohorts = prev.cohorts.includes(cohort)
        ? prev.cohorts.filter((c) => c !== cohort)
        : [...prev.cohorts, cohort];
      return { ...prev, cohorts };
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!selectedDate || !eventData.title || !eventData.startTime || !eventData.endTime) {
    Swal.fire({
      icon: "warning",
      title: "Missing Fields",
      text: "Please fill out all required fields.",
      confirmButtonColor: "#115680",
    });
    return;
  }

  const result = await Swal.fire({
    title: "Are you sure?",
    text: "Do you want to create this clinic event?",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Yes, create it",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#115680", // deep blue
    cancelButtonColor: "#fad739",  // gold
  });

  if (!result.isConfirmed) return;

  const payload = {
    ...eventData,
    date: selectedDate.toISOString().split("T")[0],
    createdAt: serverTimestamp(),
  };

  try {
    await addDoc(collection(db, "clinic_events"), payload);

    await Swal.fire({
      icon: "success",
      title: "✅ Event created successfully!",
      confirmButtonColor: "#115680",
    });

    setEventData({
      title: "",
      location: "",
      address: "",
      startTime: "",
      endTime: "",
      maxStudents: "",
      notes: "",
      cohorts: [],
    });
    setSelectedDate(null);
  } catch (error) {
    console.error("Error saving event:", error);
    await Swal.fire({
      icon: "error",
      title: "❌ Failed to create event.",
      text: "Please try again.",
      confirmButtonColor: "#115680",
    });
  }
};

const [allEvents, setAllEvents] = useState([]);

useEffect(() => {
  const unsubscribe = onSnapshot(collection(db, "clinic_events"), (snapshot) => {
    const events = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setAllEvents(events);
  });

  return () => unsubscribe();
}, []);

  return (
  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
    <h2 style={{ color: "#115680", marginBottom: "1rem" }}>
      📅 Create Clinic Event
    </h2>

    <div className={styles.calendarContainer}>
      <Calendar
        onClickDay={(date) => {
          setSelectedDate(date);

          const clickedDateStr = date.toISOString().split("T")[0];
          const matchingEvents = allEvents.filter(
            (event) => event.date === clickedDateStr
          );

          setEventsForSelectedDay(matchingEvents);
          setShowEventModal(matchingEvents.length > 0);
        }}
        value={selectedDate}
        tileContent={({ date, view }) => {
          if (view === "month") {
            const dateStr = date.toISOString().split("T")[0];
            const matchingEvents = allEvents.filter(
              (event) => event.date === dateStr
            );

            if (matchingEvents.length > 0) {
              return (
                <div className={styles.eventDotContainer}>
                  {matchingEvents.map((event, idx) => (
                    <div
                      key={idx}
                      className={styles.eventDot}
                      title={event.title}
                    />
                  ))}
                </div>
              );
            }
          }
          return null;
        }}
      />
    </div>

    {showEventModal && (
      <div
        className={styles.modalBackdrop}
        onClick={() => setShowEventModal(false)}
      >
        <div
          className={styles.modalContent}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{ color: "#115680" }}>
  Events for {selectedDate ? selectedDate.toDateString() : "Selected Date"}
</h3>

          <ul className={styles.eventList}>
            {eventsForSelectedDay.map((event, idx) => (
              <li key={idx} className={styles.eventCard}>
                <strong>{event.title}</strong>
                <p>
                  <strong>Time:</strong> {event.startTime} – {event.endTime}
                </p>
                <p>
                  <strong>Location:</strong> {event.location}
                </p>
                {event.notes && (
                  <p>
                    <strong>Notes:</strong> {event.notes}
                  </p>
                )}
                {event.cohorts?.length > 0 && (
                  <p>
                    <strong>Cohorts:</strong> {event.cohorts.join(", ")}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    )}
    <form className={styles.eventForm} onSubmit={handleSubmit}>
  {selectedDate ? (
  <h3 className={styles.formHeader}>
    Creating Event for <span>{selectedDate.toDateString()}</span>
  </h3>
) : (
  <h3 className={styles.formHeader}>Please select a date</h3>
)}


  <h3 className={styles.sectionTitle}>📝 Event Details</h3>



      <div className={styles.formGroup}>
        <label>Event Title</label>
        <input type="text" name="title" value={eventData.title} onChange={handleChange} required />
      </div>

      <div className={styles.formGroup}>
        <label>Event Location</label>
        <input type="text" name="location" value={eventData.location} onChange={handleChange} required />
      </div>

      <div className={styles.formGroup}>
        <label>Event Address</label>
        <input type="text" name="address" value={eventData.address} onChange={handleChange} />
      </div>

      <div className={styles.timeRow}>
        <div className={styles.formGroup}>
          <label>Start Time</label>
          <input type="time" name="startTime" value={eventData.startTime} onChange={handleChange} required />
        </div>
        <div className={styles.formGroup}>
          <label>End Time</label>
          <input type="time" name="endTime" value={eventData.endTime} onChange={handleChange} required />
        </div>
      </div>

      <div className={styles.formGroup}>
        <label>Max Number of Students</label>
        <input
          type="number"
          name="maxStudents"
          value={eventData.maxStudents}
          onChange={handleChange}
          required
        />
      </div>

      <div className={styles.formGroup}>
        <label>Notes</label>
        <textarea
          name="notes"
          rows="3"
          value={eventData.notes}
          onChange={handleChange}
          placeholder="Optional notes about the event"
        />
      </div>

      <div className={styles.cohortCheckboxGroup}>
        <label>Available to Cohorts:</label>
        <div className={styles.cohortCheckboxes}>
          {["OD-1", "OD-2", "OD-3", "OD-4"].map((cohort) => (
            <label key={cohort} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={eventData.cohorts.includes(cohort)}
                onChange={() => handleCohortToggle(cohort)}
              />
              {cohort}
            </label>
          ))}
        </div>
      </div>

      <button type="submit" className={styles.submitButton}>Create Event</button>
    </form>
</div>
  );
}

export default CreateClinicEvent;
